import { createColumnHelper, useTable } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import Button from "../../components/Button";
import Container from "../../components/Container";
import EmptyState from "../../components/EmptyState";
import Loader from "../../components/Loader";
import Plate from "../../components/Plate";
import SubHeader from "../../components/SubHeader";
import Table from "../../components/Table/Table";
import { sortingListingFeatures } from "../../components/Table/tableFeatures";
import { useAllUnitsQuery } from "../../queries/units/useAllUnitsQuery";
import { useAllAdministratorsQuery } from "../../queries/useAllAdministratorsQuery";
import { formatOrdererListingUnits } from "../OrdererView/OrdererView.utils";
import {
    administratorRoleLabel,
    mapAdministratorsForListing,
} from "./administratorListingUtils";
import { AdministratorListingItem } from "./administratorTypes";

const columnHelper = createColumnHelper<
    typeof sortingListingFeatures,
    AdministratorListingItem
>();
const columns = columnHelper.columns([
    columnHelper.accessor(
        (row) =>
            `${row.administrator.firstName ?? ""} ${
                row.administrator.lastName ?? ""
            }`.trim(),
        {
            id: "name",
            header: () => "Namn",
            cell: (props) => props.getValue(),
            meta: {
                getCellContext: () => {
                    return {
                        length: 4,
                    };
                },
            },
        }
    ),
    columnHelper.accessor((row) => row.administrator.municipalEmail, {
        id: "email",
        header: () => "E-post",
        enableSorting: false,
        cell: (props) => props.getValue(),
        meta: {
            getCellContext: () => {
                return {
                    length: 4,
                };
            },
        },
    }),
    columnHelper.accessor(
        (row) => administratorRoleLabel(row.administrator.municipalityRole),
        {
            id: "role",
            header: () => "Roll",
            enableSorting: false,
            cell: (props) => props.getValue(),
            meta: {
                getCellContext: () => {
                    return {
                        length: 4,
                    };
                },
            },
        }
    ),
    columnHelper.accessor(
        (row) =>
            row.administrator.municipalityRole === "ORDERER_ADMIN"
                ? formatOrdererListingUnits(row.authUnits)
                : "—",
        {
            id: "units",
            header: () => "Enhet",
            enableSorting: false,
            cell: (props) => props.getValue(),
            meta: {
                getCellContext: () => {
                    return {
                        length: 4,
                    };
                },
            },
        }
    ),
]);

const AdministratorListingView = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const allUnitsQuery = useAllUnitsQuery();
    const allAdministratorsQuery = useAllAdministratorsQuery();

    const data = useMemo(
        () =>
            allAdministratorsQuery.data &&
            mapAdministratorsForListing(
                allAdministratorsQuery.data,
                allUnitsQuery.data ?? [],
            ),
        [allAdministratorsQuery.data, allUnitsQuery.data],
    );

    const table = useTable({
        features: sortingListingFeatures,
        data: data || [],
        columns: columns,
    });

    return (
        <Container>
            <SubHeader
                title="Administratörer"
                rightContent={
                    <Button
                        size="small"
                        onClick={() => {
                            navigate(pathname + "/skapa-ny");
                        }}
                        title="Skapa ny"
                    />
                }
            />
            <Plate
                spaceBottom={true}
                overScroll
                faded={allAdministratorsQuery.isFetching}>
                {allUnitsQuery.isLoading || allAdministratorsQuery.isLoading ? (
                    <Loader />
                ) : allAdministratorsQuery.isError ? (
                    <EmptyState text="Kunde inte hämta administratörer." />
                ) : !allAdministratorsQuery.data?.length ? (
                    <EmptyState text="Det finns inga administratörer." />
                ) : (
                    <Table
                        table={table}
                        getLinkTo={(row: AdministratorListingItem) =>
                            pathname + "/" + row.administrator.uid
                        }
                    />
                )}
            </Plate>
        </Container>
    );
};

export default AdministratorListingView;
